import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Plane, Thermometer, User, CheckCircle2, XCircle, RefreshCw, ChevronDown,
  Inbox, MessageSquare, Calendar as CalIcon, TrendingUp,
} from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

const TYPE_META = {
  vacation: { label: "Vacation", icon: Plane, color: "text-sky-600", bg: "bg-sky-50", border: "border-sky-100" },
  sick:     { label: "Sick",     icon: Thermometer, color: "text-rose-600", bg: "bg-rose-50", border: "border-rose-100" },
  personal: { label: "Personal", icon: User, color: "text-violet-600", bg: "bg-violet-50", border: "border-violet-100" },
};

const fmtRange = (start, end) => {
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  const opts = { month: "short", day: "numeric" };
  if (s.toDateString() === e.toDateString()) {
    return s.toLocaleDateString([], { ...opts, year: "numeric" });
  }
  const yrSame = s.getFullYear() === e.getFullYear();
  return `${s.toLocaleDateString([], opts)} – ${e.toLocaleDateString([], { ...opts, year: yrSame ? undefined : "numeric" })}${yrSame ? `, ${s.getFullYear()}` : ""}`;
};

/**
 * Manager-facing section on the TimeClock page — lists pending time-off
 * requests at the top, and a yearly roll-up of approved days across the
 * team below.
 */
export default function TimeOffManager() {
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState([]);
  const [reviewed, setReviewed] = useState([]);
  const [summary, setSummary] = useState([]);
  const [summaryYear, setSummaryYear] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [dialog, setDialog] = useState(null); // {req, action}
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r, sum] = await Promise.all([
        api.get("/time-off?status=pending&limit=50"),
        api.get("/time-off?limit=30"),
        api.get("/time-off/summary"),
      ]);
      setPending(p.data?.items || []);
      const all = r.data?.items || [];
      setReviewed(all.filter(x => x.status !== "pending"));
      setSummary(sum.data?.rows || []);
      setSummaryYear(sum.data?.year || null);
    } catch {
      /* silent */
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDialog = (req, action) => {
    setNote("");
    setDialog({ req, action });
  };

  const submit = async () => {
    if (!dialog) return;
    const { req, action } = dialog;
    setSubmitting(true);
    try {
      await api.post(`/time-off/${req.request_id}/${action}`, { note });
      toast.success(action === "approve" ? "Time off approved" : "Request rejected");
      setDialog(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
    setSubmitting(false);
  };

  const Row = ({ req, kind }) => {
    const m = TYPE_META[req.type] || TYPE_META.personal;
    const approved = req.status === "approved";
    const rejected = req.status === "rejected";
    return (
      <div
        data-testid={`to-row-${req.request_id}`}
        className="border border-slate-100 rounded-xl p-4 bg-white">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
              style={{ backgroundColor: req.user_color || "#64748B" }}>
              {(req.user_name || "?").split(" ").map(p => p[0]).join("").slice(0, 2)}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-semibold text-slate-900 font-['Manrope']">{req.user_name}</p>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${m.bg} ${m.color} border ${m.border}`}>
                  <m.icon className="w-3 h-3" strokeWidth={2} />
                  {m.label}
                </span>
              </div>
              <div className="flex items-center gap-1.5 mt-1">
                <CalIcon className="w-3 h-3 text-slate-400" />
                <span className="text-xs text-slate-600 font-['Manrope']">
                  {fmtRange(req.start_date, req.end_date)} · <span className="font-semibold">{req.total_days} day{req.total_days === 1 ? "" : "s"}</span>
                </span>
              </div>
              {req.reason && (
                <p className="text-xs text-slate-500 mt-1.5 font-['Manrope'] flex items-start gap-1">
                  <MessageSquare className="w-3 h-3 text-slate-300 mt-0.5 flex-shrink-0" />
                  <span>{req.reason}</span>
                </p>
              )}
              {kind === "reviewed" && req.reviewer_note && (
                <p className="text-[11px] text-slate-400 mt-1 italic font-['Manrope']">
                  "{req.reviewer_note}" — {req.reviewer_name || "manager"}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {kind === "pending" ? (
              <>
                <Button size="sm" variant="outline"
                  onClick={() => openDialog(req, "reject")}
                  data-testid={`to-reject-${req.request_id}`}
                  className="h-8 border-slate-200 text-rose-600 hover:bg-rose-50 hover:border-rose-200 rounded-full px-3 text-xs">
                  <XCircle className="w-3.5 h-3.5 mr-1" />Reject
                </Button>
                <Button size="sm"
                  onClick={() => openDialog(req, "approve")}
                  data-testid={`to-approve-${req.request_id}`}
                  className="h-8 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-3 text-xs">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve
                </Button>
              </>
            ) : (
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md ${
                approved ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                : rejected ? "bg-rose-50 text-rose-700 border border-rose-100"
                : "bg-slate-100 text-slate-500"
              }`}>
                {req.status}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      data-testid="time-off-manager"
      className="bg-white border border-slate-100 rounded-2xl p-5 mb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Plane className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
          <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900">Time-Off Requests</h2>
          {pending.length > 0 && (
            <span
              data-testid="time-off-pending-badge"
              className="text-[10px] font-bold text-amber-700 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded-full">
              {pending.length} pending
            </span>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={load}
          data-testid="refresh-time-off"
          className="text-slate-400 hover:text-slate-600 h-8 px-2 rounded-full">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </div>

      {loading ? (
        <div className="h-10 bg-slate-50 rounded animate-pulse" />
      ) : (
        <>
          {pending.length > 0 ? (
            <div className="space-y-2" data-testid="time-off-pending-list">
              {pending.map(req => <Row key={req.request_id} req={req} kind="pending" />)}
            </div>
          ) : (
            <p className="text-sm text-slate-400 font-['Manrope'] py-2">All caught up — no pending time-off requests.</p>
          )}

          {/* Year-to-date team summary */}
          {summary.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <button
                onClick={() => setShowSummary(v => !v)}
                data-testid="toggle-time-off-summary"
                className="w-full flex items-center justify-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 hover:text-rose-500 transition-colors py-1.5 font-['Manrope']">
                <TrendingUp className="w-3 h-3" />
                {showSummary ? "Hide" : "Show"} team totals · {summaryYear}
                <ChevronDown className={`w-3 h-3 transition-transform ${showSummary ? "rotate-180" : ""}`} />
              </button>
              {showSummary && (
                <div className="mt-2 rounded-xl border border-slate-100 overflow-hidden" data-testid="time-off-summary-table">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                        <th className="text-left px-4 py-2 font-['Manrope']">Team member</th>
                        <th className="text-right px-3 py-2 font-['Manrope']">Vacation</th>
                        <th className="text-right px-3 py-2 font-['Manrope']">Sick</th>
                        <th className="text-right px-3 py-2 font-['Manrope']">Personal</th>
                        <th className="text-right px-4 py-2 font-['Manrope']">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.map(row => (
                        <tr key={row.user_id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`to-summary-row-${row.user_id}`}>
                          <td className="px-4 py-2 font-['Manrope']">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                                style={{ backgroundColor: row.user_color || "#64748B" }}>
                                {(row.user_name || "?").split(" ").map(p => p[0]).join("").slice(0, 2)}
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-slate-800 leading-tight">{row.user_name}</p>
                                {row.user_title && <p className="text-[10px] text-slate-400 capitalize">{row.user_title}</p>}
                              </div>
                            </div>
                          </td>
                          <td className="text-right px-3 py-2 tabular-nums text-slate-700">{row.vacation || 0}</td>
                          <td className="text-right px-3 py-2 tabular-nums text-slate-700">{row.sick || 0}</td>
                          <td className="text-right px-3 py-2 tabular-nums text-slate-700">{row.personal || 0}</td>
                          <td className="text-right px-4 py-2 tabular-nums font-semibold text-slate-900">{row.total || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {reviewed.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <button
                onClick={() => setShowHistory(v => !v)}
                data-testid="toggle-time-off-history"
                className="w-full flex items-center justify-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 hover:text-rose-500 transition-colors py-1.5 font-['Manrope']">
                <Inbox className="w-3 h-3" />
                {showHistory ? "Hide" : "Show"} recent history ({reviewed.length})
                <ChevronDown className={`w-3 h-3 transition-transform ${showHistory ? "rotate-180" : ""}`} />
              </button>
              {showHistory && (
                <div className="space-y-2 mt-2" data-testid="time-off-history-list">
                  {reviewed.map(req => <Row key={req.request_id} req={req} kind="reviewed" />)}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <Dialog open={!!dialog} onOpenChange={o => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-[440px] bg-white rounded-2xl p-8">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl font-medium">
              {dialog?.action === "approve" ? "Approve time off?" : "Reject request?"}
            </DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="space-y-4 mt-2">
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5">
                <p className="text-sm font-semibold text-slate-800 font-['Manrope']">{dialog.req.user_name}</p>
                <p className="text-xs text-slate-500 font-['Manrope'] mt-0.5 capitalize">
                  {dialog.req.total_days} day{dialog.req.total_days === 1 ? "" : "s"} · {dialog.req.type} · {fmtRange(dialog.req.start_date, dialog.req.end_date)}
                </p>
                {dialog.req.reason && (
                  <p className="text-xs text-slate-500 mt-2 italic">"{dialog.req.reason}"</p>
                )}
              </div>
              <div>
                <label className="text-xs text-slate-500 font-['Manrope'] mb-1 block">
                  Note (optional){dialog.action === "reject" ? ", tell them why" : ""}
                </label>
                <textarea
                  rows={3}
                  value={note}
                  onChange={e => setNote(e.target.value.slice(0, 500))}
                  data-testid="to-review-note"
                  placeholder={dialog.action === "approve"
                    ? "e.g. approved — enjoy your trip!"
                    : "e.g. conflicts with salon event that week"}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none outline-none focus:border-rose-300" />
              </div>
              {dialog.action === "approve" && (
                <p className="text-[11px] text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-lg p-2.5 font-['Manrope']">
                  Approving will count toward this team member's yearly totals.
                </p>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline"
              onClick={() => setDialog(null)}
              data-testid="to-review-cancel"
              className="border-slate-200 rounded-full h-10">
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={submitting}
              data-testid="to-review-confirm"
              className={`rounded-full h-10 text-white ${
                dialog?.action === "approve" ? "bg-emerald-500 hover:bg-emerald-600" : "bg-rose-500 hover:bg-rose-600"
              }`}>
              {submitting ? "Saving…" : dialog?.action === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
